import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
import ReactPlayer from 'react-player';
import { getActionLabel } from './utils/actionUtils';
import { getSportSeason, getCurrentSeason } from './utils/timeUtils';
import { getPlayerAvatarUrl } from './utils/playerUtils';
import DrawingCanvas from './components/DrawingCanvas';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Stack,
  IconButton,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GroupIcon from '@mui/icons-material/Group';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay5Icon from '@mui/icons-material/Replay5';
import Forward5Icon from '@mui/icons-material/Forward5';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SpeedIcon from '@mui/icons-material/Speed';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';

import { useStatsTheme } from './v2/theme/statsTheme';
import { CanteraButton } from './components/shared';

export default function GameCenterVideo({ groupeId }) {
  const { t } = useTranslation();
  const theme = useStatsTheme();

  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState('');

  const [allActions, setAllActions] = useState([]);
  const [filteredActions, setFilteredActions] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('all');
  const [playersInActions, setPlayersInActions] = useState([]);

  const [groupedTags, setGroupedTags] = useState([]);
  const [showTags] = useState(true); // eslint-disable-line no-unused-vars

  const [selectedActionType, setSelectedActionType] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [playing, setPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  const [playerSize, setPlayerSize] = useState({ width: 640, height: 360 });
  const playerWrapperRef = useRef(null);
  const playerRef = useRef(null);

  const [drawingEnabled, setDrawingEnabled] = useState(false);
  const [drawTool, setDrawTool] = useState('pen');
  const [drawColor, setDrawColor] = useState('#ff0000');

  const [autoChainActions, setAutoChainActions] = useState(true);
  const [currentActionStart, setCurrentActionStart] = useState(null);

  useEffect(() => {
    if (!groupeId) return;
    api.get(`/api/stats/games-with-stats/${groupeId}`)
      .then((res) => {
        const gamesData = res.data || [];
        setGames(gamesData);
        if (gamesData.length > 0 && !selectedSeason) {
          setSelectedSeason(getCurrentSeason());
        }
      })
      .catch((err) => console.error('Error al obtener los partidos:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupeId]);

  useEffect(() => {
    if (!selectedGameId) {
      setSelectedGame(null);
      setAllActions([]);
      setFilteredActions([]);
      return;
    }
    const match = games.find((g) => g.game.id === parseInt(selectedGameId, 10));
    setSelectedGame(match || null);
    if (!match) {
      setAllActions([]);
      setFilteredActions([]);
      return;
    }

    api.get(`/api/stats/actions/${match.game.id}`)
      .then((res) => {
        const data = res.data || {};
        const actions = data.actions || [];
        const { periode1 = 0, periode2 = 0, periode3 = 0, periode4 = 0 } = match.game;
        const periodOffsets = [periode1 || 0, periode2 || 0, periode3 || 0, periode4 || 0];

        const actionsWithRealTime = actions.map((act) => {
          if (act.videoTimestamp !== undefined && act.videoTimestamp !== null) {
            return { ...act, realTimeSec: act.videoTimestamp };
          }
          const periodIndex = (act.period || 1) - 1;
          const offset = periodOffsets[periodIndex] || 0;
          return { ...act, realTimeSec: act.timeSec + offset };
        });

        const regularActions = [];
        const tagsByNameMap = new Map();
        actionsWithRealTime.forEach(act => {
          if (act.action && act.action.startsWith('Tag:')) {
            const tagName = act.action.substring(4).trim();
            if (!tagsByNameMap.has(tagName)) {
              tagsByNameMap.set(tagName, { tagName, instances: [] });
            }
            const tagData = tagsByNameMap.get(tagName);
            let instance = tagData.instances.find(inst => inst.realTimeSec === act.realTimeSec);
            if (!instance) {
              instance = { realTimeSec: act.realTimeSec, period: act.period, timeSec: act.timeSec, players: [] };
              tagData.instances.push(instance);
            }
            const playerInfo = match.players?.find(p => p.player_id === act.player_id);
            instance.players.push({ id: act.player_id, name: playerInfo?.nom || `Player ${act.player_id}` });
          } else {
            regularActions.push(act);
          }
        });

        const groupedTagsArray = Array.from(tagsByNameMap.values()).map(tagData => {
          const allPlayerIds = new Set();
          tagData.instances.forEach(inst => inst.players.forEach(p => allPlayerIds.add(p.id)));
          const allPlayers = [];
          allPlayerIds.forEach(id => {
            const playerInfo = match.players?.find(p => p.player_id === id);
            allPlayers.push({ id, name: playerInfo?.nom || `Player ${id}` });
          });
          return {
            tagName: tagData.tagName,
            action: `Tag: ${tagData.tagName}`,
            instances: tagData.instances,
            instanceCount: tagData.instances.length,
            allPlayers,
            isGroupedTag: true,
          };
        });

        setGroupedTags(groupedTagsArray);
        setAllActions(regularActions);
        setFilteredActions(regularActions);
      })
      .catch((err) => console.error('Error al obtener las acciones:', err));
  }, [selectedGameId, games]);

  useEffect(() => {
    const uniquePlayers = [];
    const mapPlayers = {};
    allActions.forEach((act) => {
      if (!mapPlayers[act.player_id]) {
        mapPlayers[act.player_id] = true;
        uniquePlayers.push(act.player_id);
      }
    });
    setPlayersInActions(uniquePlayers);
  }, [allActions]);

  useEffect(() => {
    if (selectedPlayerId === 'all') {
      setFilteredActions(allActions);
    } else {
      const pid = parseInt(selectedPlayerId, 10);
      setFilteredActions(allActions.filter((a) => a.player_id === pid));
    }
    setSelectedActionType(null);
    setCurrentIndex(0);
    setCurrentActionStart(null);
  }, [selectedPlayerId, allActions]);

  useEffect(() => {
    if (!playerWrapperRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setPlayerSize({ width, height });
      }
    });
    observer.observe(playerWrapperRef.current);
    return () => observer.disconnect();
  }, []);

  const getTimesForActionType = (actionType) => {
    if (actionType && actionType.startsWith('Tag:')) {
      const matchingTag = groupedTags.find((tg) => tg.action === actionType);
      if (matchingTag) {
        const times = [];
        matchingTag.instances.forEach(instance => times.push(instance.realTimeSec));
        return times.sort((a, b) => a - b);
      }
      return [];
    }
    return filteredActions
      .filter((a) => a.action === actionType)
      .map((a) => a.realTimeSec)
      .sort((a, b) => a - b);
  };

  const seekToTime = (sec) => {
    if (playerRef.current) {
      playerRef.current.seekTo(sec, 'seconds');
      setPlaying(true);
    }
  };

  const handleSelectActionType = (type) => {
    setSelectedActionType(type);
    setCurrentIndex(0);
    const times = getTimesForActionType(type);
    if (times.length > 0) {
      seekToTime(times[0]);
      setCurrentActionStart(times[0]);
      setPlaying(true);
    }
  };

  const handlePrev = () => {
    if (!selectedActionType) return;
    const times = getTimesForActionType(selectedActionType);
    if (times.length === 0) return;
    setCurrentIndex((prev) => {
      const newIndex = Math.max(0, prev - 1);
      seekToTime(times[newIndex]);
      setCurrentActionStart(times[newIndex]);
      return newIndex;
    });
  };

  const handleNext = () => {
    if (!selectedActionType) return;
    const times = getTimesForActionType(selectedActionType);
    if (times.length === 0) return;
    setCurrentIndex((prev) => {
      const newIndex = Math.min(times.length - 1, prev + 1);
      seekToTime(times[newIndex]);
      setCurrentActionStart(times[newIndex]);
      return newIndex;
    });
  };

  const skipTime = (delta) => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(currentTime + delta, 'seconds');
    }
  };

  const handleClearDrawing = () => {
    const canvas = playerWrapperRef.current?.querySelector('canvas');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleProgress = (progressState) => {
    if (!autoChainActions || !selectedActionType || !playing) return;
    const times = getTimesForActionType(selectedActionType);
    if (times.length === 0) return;
    if (currentActionStart !== null && progressState.playedSeconds >= currentActionStart + 10) {
      handleNext();
    }
  };

  // Partidos agrupés por saison
  const gamesBySeason = (() => {
    const grouped = {};
    games.forEach((g) => {
      const season = getSportSeason(g.game.game_date);
      if (!grouped[season]) grouped[season] = [];
      grouped[season].push(g);
    });
    Object.keys(grouped).forEach((season) => {
      grouped[season].sort((a, b) => new Date(b.game.game_date) - new Date(a.game.game_date));
    });
    return grouped;
  })();

  const availableSeasons = (() => {
    const seasons = new Set();
    games.forEach((g) => seasons.add(getSportSeason(g.game.game_date)));
    return Array.from(seasons).sort((a, b) => {
      const [yearA] = a.split('-').map(Number);
      const [yearB] = b.split('-').map(Number);
      return yearB - yearA;
    });
  })();

  const filteredGames = selectedSeason ? (gamesBySeason[selectedSeason] || []) : games;

  // Stats chips — utilisent couleurs sémantiques
  const statChipConfigs = (stats) => [
    { label: 'PTS', value: stats.points, color: theme.accent },
    { label: 'REB', value: stats.rebounds, color: theme.actionColors.rebound.def },
    { label: 'AST', value: stats.assists, color: theme.actionColors.ballHandling.ast },
    { label: 'STL', value: stats.steals, color: theme.actionColors.ballHandling.stl },
    { label: 'BLK', value: stats.blocks, color: theme.actionColors.defense.blk },
  ];

  // MUI Select styling for dark theme
  const selectSx = {
    bgcolor: theme.surfaces.base,
    color: theme.text.primary,
    fontSize: 13,
    borderRadius: `${theme.radius.md}px`,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.surfaces.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.accentBorder },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.accent, borderWidth: 2 },
    '& .MuiSelect-icon': { color: theme.text.secondary },
  };
  const menuItemSx = {
    fontSize: 13,
    '&.Mui-selected': { bgcolor: theme.accentSoft, color: theme.accent },
    '&:hover': { bgcolor: theme.accentSofter },
  };

  // Reusable label for selects
  const FieldLabel = ({ children }) => (
    <Typography sx={{ color: theme.text.muted, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>
      {children}
    </Typography>
  );

  const visibleTags = groupedTags.filter((tag) => {
    if (selectedPlayerId === 'all') return true;
    const pid = parseInt(selectedPlayerId, 10);
    return tag.allPlayers.some(p => p.id === pid);
  });

  // Color palette for quick color buttons
  const colorSwatches = [
    { hex: '#ef4444', label: 'Red' },
    { hex: '#3b82f6', label: 'Blue' },
    { hex: '#22c55e', label: 'Green' },
    { hex: '#f59e0b', label: 'Orange' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 120px)',
        minHeight: 520,
        overflow: 'hidden',
        bgcolor: theme.surfaces.base,
        border: `1px solid ${theme.surfaces.border}`,
        borderRadius: `${theme.radius.lg}px`,
      }}
    >
      {/* SIDEBAR - filtres + tags + actions */}
      <Box
        sx={{
          width: { xs: 280, md: 340 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.surfaces.elevated,
          borderRight: `1px solid ${theme.surfaces.border}`,
          overflow: 'hidden',
        }}
      >
        {/* Filtres */}
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.surfaces.border}`, bgcolor: theme.surfaces.base }}>
          {availableSeasons.length > 0 && (
            <Box sx={{ mb: 1.25 }}>
              <FieldLabel>{t('gameCenterVideo.season')}</FieldLabel>
              <Select
                fullWidth size="small" value={selectedSeason}
                onChange={(e) => { setSelectedSeason(e.target.value); setSelectedGameId(''); }}
                sx={selectSx}
                displayEmpty
                renderValue={(v) => v || t('gameCenterVideo.allSeasons')}
              >
                <MenuItem value="" sx={menuItemSx}>{t('gameCenterVideo.allSeasons')}</MenuItem>
                {availableSeasons.map((season) => (
                  <MenuItem key={season} value={season} sx={menuItemSx}>
                    {season} ({gamesBySeason[season]?.length || 0})
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          <Box sx={{ mb: 1.25 }}>
            <FieldLabel>{t('gameCenterVideo.match')}</FieldLabel>
            <Select
              fullWidth size="small" value={selectedGameId}
              onChange={(e) => setSelectedGameId(e.target.value)}
              sx={selectSx}
              displayEmpty
              renderValue={(v) => {
                if (!v) return t('gameCenterVideo.noMatch');
                const g = filteredGames.find(fg => fg.game.id === v);
                return g ? `${g.game.game_label} vs ${g.game.adversary_name}` : '';
              }}
            >
              <MenuItem value="" sx={menuItemSx}>{t('gameCenterVideo.noMatch')}</MenuItem>
              {filteredGames.map((g) => (
                <MenuItem key={g.game.id} value={g.game.id} sx={menuItemSx}>
                  {g.game.game_label} vs {g.game.adversary_name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {selectedGame && (
            <Box>
              <FieldLabel>{t('gameCenterVideo.player')}</FieldLabel>
              <Select
                fullWidth size="small" value={selectedPlayerId}
                onChange={(e) => setSelectedPlayerId(e.target.value)}
                sx={selectSx}
              >
                <MenuItem value="all" sx={menuItemSx}>{t('gameCenterVideo.allPlayers')}</MenuItem>
                {playersInActions.map((pid) => {
                  const pInfo = selectedGame.players.find((pl) => pl.player_id === pid) || {};
                  return (
                    <MenuItem key={pid} value={pid} sx={menuItemSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={getPlayerAvatarUrl(pid)} alt={pInfo.nom || `#${pid}`} sx={{ width: 22, height: 22 }} />
                        <Typography sx={{ fontSize: 13 }}>{pInfo.nom || `#${pid}`}</Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          )}

          {/* Stats compactes */}
          {selectedGame && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {(() => {
                if (selectedPlayerId === 'all') {
                  return selectedGame.teamStats ? statChipConfigs(selectedGame.teamStats) : [];
                }
                const pid = parseInt(selectedPlayerId, 10);
                const ps = selectedGame.players.find((p) => p.player_id === pid);
                return ps ? statChipConfigs(ps) : [];
              })().map((s, i) => (
                <Box
                  key={i}
                  sx={{
                    px: 0.75, py: 0.25,
                    bgcolor: theme.withAlpha(s.color, 0.14),
                    border: `1px solid ${theme.withAlpha(s.color, 0.3)}`,
                    borderRadius: `${theme.radius.sm}px`,
                    display: 'flex', alignItems: 'center', gap: 0.5,
                  }}
                >
                  <Typography sx={{ color: theme.text.muted, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em' }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{ color: s.color, fontSize: 12, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                    {s.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Tags et actions scrollables */}
        {selectedGame && (
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
            {visibleTags.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                  <LocalOfferIcon sx={{ color: theme.accent, fontSize: 16 }} />
                  <Typography sx={{ color: theme.text.primary, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {t('gameCenterVideo.teamTags')}
                  </Typography>
                </Stack>
                <Stack spacing={0.75}>
                  {visibleTags.map((tag, idx) => {
                    const isSelected = selectedActionType === tag.action;
                    return (
                      <Tooltip
                        key={`tag_${idx}`}
                        arrow placement="left"
                        title={
                          <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>
                              {t('gameCenterVideo.players')}:
                            </Typography>
                            <Stack spacing={0.25}>
                              {tag.allPlayers.slice(0, 8).map((player, i) => (
                                <Typography key={i} sx={{ fontSize: 11 }}>• {player.name}</Typography>
                              ))}
                              {tag.allPlayers.length > 8 && (
                                <Typography sx={{ fontSize: 11, fontStyle: 'italic' }}>
                                  {t('gameCenterVideo.andMore', { count: tag.allPlayers.length - 8 })}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        }
                      >
                        <Box
                          onClick={() => handleSelectActionType(tag.action)}
                          sx={{
                            px: 1.25, py: 0.75,
                            cursor: 'pointer',
                            bgcolor: isSelected ? theme.accent : theme.surfaces.floating,
                            color: isSelected ? '#fff' : theme.text.primary,
                            border: `1px solid ${isSelected ? theme.accent : theme.surfaces.border}`,
                            borderRadius: `${theme.radius.md}px`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 1,
                            transition: theme.motion.fast,
                            '&:hover': {
                              borderColor: theme.accentBorder,
                              bgcolor: isSelected ? theme.accent : theme.accentSoft,
                            },
                          }}
                        >
                          <Typography sx={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tag.tagName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            {tag.instanceCount > 1 && (
                              <Chip
                                label={`×${tag.instanceCount}`} size="small"
                                sx={{
                                  height: 18, fontSize: 10, fontWeight: 700,
                                  bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : theme.withAlpha(theme.actionColors.warning, 0.18),
                                  color: isSelected ? '#fff' : theme.actionColors.warning,
                                  border: 'none',
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            )}
                            <Chip
                              icon={<GroupIcon sx={{ fontSize: '12px !important', color: 'inherit' }} />}
                              label={tag.allPlayers.length} size="small"
                              sx={{
                                height: 18, fontSize: 10, fontWeight: 700,
                                bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : theme.surfaces.base,
                                color: isSelected ? '#fff' : theme.text.secondary,
                                border: `1px solid ${isSelected ? 'transparent' : theme.surfaces.border}`,
                                '& .MuiChip-label': { px: 0.5 },
                              }}
                            />
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography sx={{ color: theme.text.primary, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 1 }}>
                {t('gameCenterVideo.individualActions')}
              </Typography>
              <Stack spacing={0.5}>
                {Array.from(new Set(filteredActions.map((a) => a.action))).map((type) => {
                  const count = filteredActions.filter((a) => a.action === type).length;
                  const isSelected = selectedActionType === type;
                  const displayLabel = getActionLabel(type, t);
                  return (
                    <Box
                      key={type}
                      onClick={() => handleSelectActionType(type)}
                      sx={{
                        px: 1.25, py: 0.75,
                        cursor: 'pointer',
                        bgcolor: isSelected ? theme.accent : theme.surfaces.floating,
                        color: isSelected ? '#fff' : theme.text.primary,
                        border: `1px solid ${isSelected ? theme.accent : theme.surfaces.border}`,
                        borderRadius: `${theme.radius.md}px`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 1,
                        transition: theme.motion.fast,
                        '&:hover': {
                          borderColor: theme.accentBorder,
                          bgcolor: isSelected ? theme.accent : theme.accentSoft,
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: isSelected ? 700 : 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {displayLabel}
                      </Typography>
                      <Box
                        sx={{
                          minWidth: 28, px: 0.75, py: 0.1,
                          textAlign: 'center',
                          bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : theme.surfaces.base,
                          color: isSelected ? '#fff' : theme.text.secondary,
                          border: `1px solid ${isSelected ? 'transparent' : theme.surfaces.border}`,
                          borderRadius: `${theme.radius.sm}px`,
                          fontSize: 11, fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {count}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>

      {/* ZONE PRINCIPALE */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: theme.surfaces.base }}>
        {selectedGame ? (
          <>
            {/* Video */}
            <Box sx={{ flex: 1, position: 'relative', bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box ref={playerWrapperRef} sx={{ position: 'relative', width: '100%', height: '100%' }}>
                {selectedGame.game.url ? (
                  <ReactPlayer
                    ref={playerRef}
                    url={selectedGame.game.url}
                    controls
                    playing={playing}
                    playbackRate={playbackRate}
                    width="100%"
                    height="100%"
                    onReady={() => {
                      if (playerWrapperRef.current) {
                        const rect = playerWrapperRef.current.getBoundingClientRect();
                        setPlayerSize({ width: rect.width, height: rect.height });
                      }
                    }}
                    onProgress={handleProgress}
                  />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, p: 3 }}>
                    <VideocamOffIcon sx={{ color: theme.text.muted, fontSize: 48 }} />
                    <Typography sx={{ color: theme.text.secondary, fontSize: 14, textAlign: 'center' }}>
                      {t('gameCenterVideo.noVideoUrl')}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    position: 'absolute', top: 0, left: 0,
                    width: playerSize.width, height: playerSize.height,
                    pointerEvents: drawingEnabled ? 'auto' : 'none',
                    overflow: 'hidden',
                  }}
                >
                  <DrawingCanvas
                    width={playerSize.width} height={playerSize.height}
                    isDrawingEnabled={drawingEnabled}
                    tool={drawTool} color={drawColor}
                  />
                </Box>
              </Box>
            </Box>

            {/* Control bar */}
            <Box
              sx={{
                borderTop: `1px solid ${theme.surfaces.border}`,
                bgcolor: theme.surfaces.elevated,
                p: 1.25,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {/* Playback */}
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <CanteraButton
                  variant={playing ? 'secondary' : 'primary'}
                  size="sm"
                  startIcon={playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                  onClick={() => setPlaying(!playing)}
                >
                  {playing ? t('gameCenterVideo.pause') : t('gameCenterVideo.play')}
                </CanteraButton>
                <CanteraButton
                  variant={playbackRate === 0.5 ? 'primary' : 'ghost'}
                  size="sm"
                  startIcon={<SpeedIcon fontSize="small" />}
                  onClick={() => setPlaybackRate(0.5)}
                >
                  0.5×
                </CanteraButton>
                <CanteraButton
                  variant={playbackRate === 1 ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setPlaybackRate(1)}
                >
                  1×
                </CanteraButton>
                <CanteraButton variant="ghost" size="sm" startIcon={<Replay5Icon fontSize="small" />} onClick={() => skipTime(-5)}>
                  -5s
                </CanteraButton>
                <CanteraButton variant="ghost" size="sm" startIcon={<Forward5Icon fontSize="small" />} onClick={() => skipTime(5)}>
                  +5s
                </CanteraButton>
              </Stack>

              {/* Nav + drawing */}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="space-between">
                {selectedActionType && (
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Box
                      sx={{
                        px: 1.25, py: 0.5,
                        bgcolor: theme.accentSoft,
                        border: `1px solid ${theme.accentBorder}`,
                        borderRadius: `${theme.radius.md}px`,
                      }}
                    >
                      <Typography sx={{ color: theme.accent, fontSize: 12, fontWeight: 700, letterSpacing: '0.02em' }}>
                        {selectedActionType}
                      </Typography>
                    </Box>
                    <CanteraButton variant="secondary" size="sm" startIcon={<SkipPreviousIcon fontSize="small" />} onClick={handlePrev}>
                      {t('gameCenterVideo.prev')}
                    </CanteraButton>
                    <CanteraButton variant="secondary" size="sm" endIcon={<SkipNextIcon fontSize="small" />} onClick={handleNext}>
                      {t('gameCenterVideo.next')}
                    </CanteraButton>
                    <Typography sx={{ color: theme.text.secondary, fontSize: 12, fontWeight: 600, px: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                      {currentIndex + 1} / {getTimesForActionType(selectedActionType).length}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={autoChainActions}
                          onChange={(e) => setAutoChainActions(e.target.checked)}
                          size="small"
                          sx={{
                            color: theme.text.secondary,
                            '&.Mui-checked': { color: theme.accent },
                          }}
                        />
                      }
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <AutoModeIcon sx={{ fontSize: 14, color: theme.text.muted }} />
                          <Typography sx={{ color: theme.text.secondary, fontSize: 11, fontWeight: 600 }}>
                            {t('gameCenterVideo.auto')}
                          </Typography>
                        </Stack>
                      }
                      sx={{ ml: 0 }}
                    />
                  </Stack>
                )}

                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <CanteraButton
                    variant={drawingEnabled ? 'primary' : 'ghost'}
                    size="sm"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => setDrawingEnabled(!drawingEnabled)}
                  >
                    {drawingEnabled ? 'ON' : 'OFF'}
                  </CanteraButton>
                  {drawingEnabled && (
                    <>
                      <CanteraButton
                        variant={drawTool === 'pen' ? 'primary' : 'ghost'}
                        size="sm" onClick={() => setDrawTool('pen')}
                      >
                        {t('gameCenterVideo.pen')}
                      </CanteraButton>
                      <CanteraButton
                        variant={drawTool === 'eraser' ? 'primary' : 'ghost'}
                        size="sm" onClick={() => setDrawTool('eraser')}
                      >
                        {t('gameCenterVideo.erase')}
                      </CanteraButton>
                      {colorSwatches.map((sw) => (
                        <IconButton
                          key={sw.hex}
                          onClick={() => { setDrawTool('pen'); setDrawColor(sw.hex); }}
                          size="small"
                          aria-label={sw.label}
                          sx={{
                            width: 30, height: 30,
                            bgcolor: sw.hex,
                            border: `2px solid ${drawColor === sw.hex ? theme.text.primary : 'transparent'}`,
                            borderRadius: `${theme.radius.sm}px`,
                            transition: theme.motion.fast,
                            '&:hover': { bgcolor: sw.hex, transform: 'scale(1.08)' },
                          }}
                        />
                      ))}
                      <CanteraButton
                        variant="ghost" size="sm"
                        startIcon={<DeleteOutlineIcon fontSize="small" />}
                        onClick={handleClearDrawing}
                      >
                        {t('gameCenterVideo.clear')}
                      </CanteraButton>
                    </>
                  )}
                </Stack>
              </Stack>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 1.5,
              bgcolor: theme.surfaces.base,
            }}
          >
            <VideocamOffIcon sx={{ color: theme.text.muted, fontSize: 56 }} />
            <Typography sx={{ color: theme.text.secondary, fontSize: 15, fontWeight: 500, textAlign: 'center', px: 2 }}>
              {t('gameCenterVideo.selectMatchToStart')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
